import {
    Autocomplete,
    Box,
    FormControlLabel,
    Grid,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DescriptionIcon from "@mui/icons-material/Description";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SaveIcon from "@mui/icons-material/Save";
import { observer } from "mobx-react";
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useNavigate, useParams } from "react-router";
import AcxButton from "components/UI/AcxButton";
import AcxMainTextField, {
    AcxMainTextFieldRef,
} from "components/UI/AcxMainTextField";
import AcxLoadingIndicator from "components/UI/AcxLoadingIndicator";
import AcxSelectMulti from "components/UI/Select/BaseSelectComponents/AcxSelectMulti";
import AcxSwitch from "components/UI/AcxSwitch";
import { AcxMenuItem, AcxMenuV2 } from "components/UI/Menu/AcxMenu";
import { getCaretCoordinates } from "components/Classifiers/LLMClassifiers/Components/LogicChainBuilder/utils";
import { useStore } from "utils/useStore";
import AgentSkillsStore from "components/AgentSkills/Stores/AgentSkillsStore";
import AgentSkillTestDialog from "components/AgentSkills/Views/AgentSkillTestDialog";
import {
    AgentSkillFile,
    AgentSkillScope,
    AgentSkillToolDefinition,
    emptySaveAgentSkillRequest,
    SaveAgentSkillRequest,
} from "components/AgentSkills/Models/AgentSkill";
import {
    ensureReferencedToolsAllowed,
    formatAllowedTools,
    parseAllowedTools,
} from "components/AgentSkills/Models/AgentSkillAllowedTools";

interface Props {
    skillId?: string | null;
    onClose?: () => void;
    onSaved?: () => void;
}

type ReferenceType = "skill" | "tool" | "file";

type ReferenceTarget =
    | { kind: "skillText" }
    | { kind: "fileBody"; index: number };

type ReferenceMenuState = {
    target: ReferenceTarget;
    startIndex: number;
    endIndex: number;
    position: { top: number; left: number };
    search: string;
} | null;

interface ParsedReference {
    type: ReferenceType;
    name: string;
}

const referenceRegex = /\{\{\s*(skill|tool|file)\s*:\s*([^}]*)\s*\}\}/gi;
const referenceHint = "Type @ to reference skills, tools, and files.";

const skillScopeOptions: { value: AgentSkillScope; label: string }[] = [
    { value: "KboChat", label: "KBo Chat" },
    { value: "KboFlow", label: "KBo Flow" },
];

const parseReferences = (value: string): ParsedReference[] => {
    const references: ParsedReference[] = [];
    let match: RegExpExecArray | null;
    referenceRegex.lastIndex = 0;
    while ((match = referenceRegex.exec(value ?? "")) !== null) {
        references.push({
            type: match[1].toLowerCase() as ReferenceType,
            name: match[2].trim(),
        });
    }
    return references;
};

const isSkillMarkdown = (path?: string | null) =>
    path?.trim().toLowerCase() === "skill.md";

const isValidRelativePath = (path?: string | null) => {
    const trimmed = path?.trim() ?? "";
    if (!trimmed || trimmed.length > 256) return false;
    if (
        trimmed.startsWith("/") ||
        trimmed.startsWith("\\") ||
        trimmed.includes(":")
    ) {
        return false;
    }

    return trimmed
        .split(/[\\/]/)
        .every((segment) => segment && segment !== "." && segment !== "..");
};

const AgentSkillEditorPage: React.FC<Props> = observer(
    ({ skillId, onClose, onSaved }) => {
        const store = useStore(AgentSkillsStore);
        const navigate = useNavigate();
        const { skillId: routeSkillId } = useParams<{ skillId?: string }>();
        const resolvedSkillId = skillId ?? routeSkillId ?? null;
        const skillTextRef = useRef<AcxMainTextFieldRef>(null);
        const fileBodyRefs = useRef<Record<number, AcxMainTextFieldRef | null>>(
            {},
        );
        const referenceMenuRef = useRef<HTMLUListElement>(null);
        const datasetReplacementSkillIdRef = useRef<string | null>(null);
        const [form, setForm] = useState<SaveAgentSkillRequest>(
            emptySaveAgentSkillRequest(),
        );
        const [activeSkillId, setActiveSkillId] = useState<string | null>(
            resolvedSkillId,
        );
        const [loading, setLoading] = useState(false);
        const [referenceMenu, setReferenceMenu] =
            useState<ReferenceMenuState>(null);
        const [testDialogOpen, setTestDialogOpen] = useState(false);
        const [selectedFileKey, setSelectedFileKey] = useState<string>("skill");

        const saving = store.getTaskLoading(AgentSkillsStore.Tasks.Save);
        const toolsLoading = store.getTaskLoading(
            AgentSkillsStore.Tasks.LoadAvailableTools,
        );

        const selectedAllowedTools = useMemo(() => {
            const reconciled = ensureReferencedToolsAllowed(
                form,
                store.availableToolNames,
            );
            return parseAllowedTools(reconciled.frontmatter?.allowedTools);
        }, [form, store.availableToolNames]);

        const allowedToolOptions = useMemo(() => {
            const toolsByName = new Map(
                store.availableTools.map((tool) => [
                    tool.name.toLowerCase(),
                    tool,
                ]),
            );

            selectedAllowedTools.forEach((name) => {
                const key = name.toLowerCase();
                if (!toolsByName.has(key)) {
                    toolsByName.set(key, {
                        name,
                        friendlyName: name,
                        description: "",
                    });
                }
            });

            return Array.from(toolsByName.values()).sort((a, b) =>
                a.friendlyName.localeCompare(b.friendlyName),
            );
        }, [store.availableTools, selectedAllowedTools]);

        const selectedAllowedToolOptions = useMemo(
            () =>
                selectedAllowedTools
                    .map((name) =>
                        allowedToolOptions.find(
                            (tool) =>
                                tool.name.toLowerCase() === name.toLowerCase(),
                        ),
                    )
                    .filter(
                        (tool): tool is AgentSkillToolDefinition =>
                            tool != null,
                    ),
            [allowedToolOptions, selectedAllowedTools],
        );

        const skillReferenceOptions = useMemo(
            () =>
                store.skills
                    .filter((skill) => skill.id !== activeSkillId)
                    .map((skill) => skill.name)
                    .filter(Boolean)
                    .sort((a, b) => a.localeCompare(b)),
            [activeSkillId, store.skills],
        );

        const fileReferenceOptions = useMemo(
            () =>
                form.files
                    .map((file) => file.relativePath?.trim())
                    .filter((path): path is string => Boolean(path))
                    .filter((path) => !isSkillMarkdown(path))
                    .sort((a, b) => a.localeCompare(b)),
            [form.files],
        );

        const filePathIssues = useMemo(() => {
            const pathCounts = form.files.reduce<Record<string, number>>(
                (acc, file) => {
                    const key = file.relativePath?.trim().toLowerCase();
                    if (key) acc[key] = (acc[key] ?? 0) + 1;
                    return acc;
                },
                {},
            );

            return form.files.map((file) => {
                const path = file.relativePath?.trim() ?? "";
                if (!path) return "File path is required.";
                if (isSkillMarkdown(path)) return "SKILL.md is reserved.";
                if (!isValidRelativePath(path))
                    return "Use a relative path without '..', roots, or ':'.";
                if ((pathCounts[path.toLowerCase()] ?? 0) > 1)
                    return "Two files cannot share the same path.";
                return null;
            });
        }, [form.files]);

        const selectedFileIndex = useMemo(() => {
            if (!selectedFileKey.startsWith("file:")) return null;
            const index = Number(selectedFileKey.slice("file:".length));
            return Number.isInteger(index) && index >= 0 ? index : null;
        }, [selectedFileKey]);

        const selectedFile =
            selectedFileIndex === null
                ? null
                : form.files[selectedFileIndex] ?? null;

        const selectedReferenceTarget: ReferenceTarget =
            selectedFileIndex === null
                ? { kind: "skillText" }
                : { kind: "fileBody", index: selectedFileIndex };

        const selectedEditorValue =
            selectedFileIndex === null
                ? form.skillText
                : selectedFile?.body ?? "";

        const selectedFilePathIssue =
            selectedFileIndex === null
                ? null
                : filePathIssues[selectedFileIndex] ?? null;

        const duplicateName = useMemo(() => {
            const normalized = form.name.trim().toLowerCase();
            return (
                normalized.length > 0 &&
                store.skills.some(
                    (skill) =>
                        skill.id !== activeSkillId &&
                        skill.name.trim().toLowerCase() === normalized,
                )
            );
        }, [activeSkillId, form.name, store.skills]);

        const referenceIssues = useMemo(() => {
            const skillNames = new Set(
                store.skills
                    .filter((skill) => skill.id !== activeSkillId)
                    .map((skill) => skill.name.trim().toLowerCase()),
            );
            const toolNames = new Set(
                store.availableToolNames.map((tool) =>
                    tool.trim().toLowerCase(),
                ),
            );
            const currentName = form.name.trim().toLowerCase();
            const fileNames = new Set(
                form.files
                    .map((file) => file.relativePath?.trim().toLowerCase())
                    .filter(Boolean),
            );
            const texts = [
                form.skillText,
                ...form.files.map((file) => file.body ?? ""),
            ];
            return texts
                .flatMap(parseReferences)
                .map((reference) => {
                    if (!reference.name) {
                        return `${
                            reference.type === "skill"
                                ? "Skill"
                                : reference.type === "tool"
                                ? "Tool"
                                : "File"
                        } reference is missing a name.`;
                    }
                    if (
                        reference.type === "skill" &&
                        reference.name.trim().toLowerCase() === currentName
                    ) {
                        return "A skill cannot reference itself.";
                    }
                    if (
                        reference.type === "skill" &&
                        !skillNames.has(reference.name.trim().toLowerCase())
                    ) {
                        return `Unknown skill reference: ${reference.name}`;
                    }
                    if (
                        reference.type === "tool" &&
                        !toolNames.has(reference.name.trim().toLowerCase())
                    ) {
                        return `Unknown tool reference: ${reference.name}`;
                    }
                    if (reference.type === "file") {
                        const path = reference.name.trim();
                        if (isSkillMarkdown(path)) {
                            return "SKILL.md is the skill text and cannot be referenced as a file.";
                        }
                        if (!isValidRelativePath(path)) {
                            return `Invalid file reference: ${reference.name}`;
                        }
                        if (!fileNames.has(path.toLowerCase())) {
                            return `Unknown file reference: ${reference.name}`;
                        }
                    }
                    return null;
                })
                .filter((issue): issue is string => Boolean(issue));
        }, [
            form.name,
            form.files,
            form.skillText,
            activeSkillId,
            store.availableToolNames,
            store.skills,
        ]);

        useEffect(() => {
            store.loadSkills();
            store.loadAvailableTools();
            setActiveSkillId(resolvedSkillId);
            const isDatasetReplacement =
                datasetReplacementSkillIdRef.current === resolvedSkillId;
            if (isDatasetReplacement) {
                datasetReplacementSkillIdRef.current = null;
                setLoading(false);
                return;
            }
            setTestDialogOpen(false);
            let cancelled = false;
            if (!resolvedSkillId) {
                setForm(emptySaveAgentSkillRequest());
                setSelectedFileKey("skill");
                setLoading(false);
                return;
            }
            setSelectedFileKey("skill");
            setLoading(true);
            store
                .getSkill(resolvedSkillId)
                .then((skill) => {
                    if (cancelled || !skill) return;
                    setForm({
                        name: skill.name,
                        description: skill.description,
                        skillText: skill.skillText,
                        isPublished: skill.isPublished,
                        scopes: skill.scopes ?? [],
                        frontmatter: skill.frontmatter ?? {
                            allowedTools: "",
                            metadata: {},
                        },
                        files: skill.files ?? [],
                        testCases: skill.testCases ?? [],
                        configuration: skill.configuration ?? {},
                    });
                })
                .finally(() => {
                    if (!cancelled) setLoading(false);
                });
            return () => {
                cancelled = true;
            };
        }, [resolvedSkillId, store]);

        useEffect(() => {
            if (
                selectedFileIndex !== null &&
                selectedFileIndex >= form.files.length
            ) {
                setSelectedFileKey("skill");
            }
        }, [form.files.length, selectedFileIndex]);

        const isValid = useMemo(
            () =>
                form.name.trim().length > 0 &&
                form.description.trim().length > 0 &&
                form.skillText.trim().length > 0 &&
                form.scopes.length > 0 &&
                !duplicateName &&
                filePathIssues.every((issue) => !issue) &&
                referenceIssues.length === 0,
            [duplicateName, filePathIssues, form, referenceIssues.length],
        );

        const update = (patch: Partial<SaveAgentSkillRequest>) =>
            setForm((prev) => ({ ...prev, ...patch }));

        const currentRequest = useCallback(
            () => ensureReferencedToolsAllowed(form, store.availableToolNames),
            [form, store.availableToolNames],
        );

        const close = useCallback(() => {
            if (onClose) {
                onClose();
                return;
            }

            if (store.currentOrganizationId) {
                navigate(
                    `/admin/organizations/${store.currentOrganizationId}/agent-skills`,
                );
                return;
            }

            navigate("..");
        }, [navigate, onClose, store.currentOrganizationId]);

        const save = useCallback(async () => {
            const saved = await store.saveSkill(
                currentRequest(),
                activeSkillId ?? undefined,
            );
            if (saved) {
                onSaved?.();
                close();
            }
        }, [store, currentRequest, activeSkillId, onSaved, close]);

        const handleDatasetVersionSaved = useCallback(
            (saved: { id: string }) => {
                datasetReplacementSkillIdRef.current = saved.id;
                setActiveSkillId(saved.id);
                if (store.currentOrganizationId) {
                    navigate(
                        `/admin/organizations/${store.currentOrganizationId}/agent-skills/${saved.id}`,
                        { replace: true },
                    );
                    return;
                }

                navigate(`../${saved.id}`, { relative: "path", replace: true });
            },
            [navigate, store.currentOrganizationId],
        );

        const updateFile = (index: number, patch: Partial<AgentSkillFile>) => {
            setForm((prev) => {
                const files = [...prev.files];
                files[index] = { ...files[index], ...patch };
                return { ...prev, files };
            });
        };

        const addFile = () => {
            const existingPaths = new Set(
                form.files.map((file) => file.relativePath.toLowerCase()),
            );
            const newIndex = form.files.length;
            let nextNumber = form.files.length + 1;
            let relativePath = `references/file-${nextNumber}.md`;
            while (existingPaths.has(relativePath.toLowerCase())) {
                nextNumber += 1;
                relativePath = `references/file-${nextNumber}.md`;
            }

            update({
                files: [
                    ...form.files,
                    {
                        relativePath,
                        body: "",
                        frontmatter: { metadata: {} },
                    },
                ],
            });
            setSelectedFileKey(`file:${newIndex}`);
        };

        const removeFile = (index: number) => {
            update({ files: form.files.filter((_, i) => i !== index) });
            setSelectedFileKey((current) => {
                if (!current.startsWith("file:")) return current;
                const currentIndex = Number(current.slice("file:".length));
                if (!Number.isInteger(currentIndex)) return "skill";
                if (currentIndex === index) return "skill";
                if (currentIndex > index) return `file:${currentIndex - 1}`;
                return current;
            });
        };

        const closeReferenceMenu = () => {
            setReferenceMenu(null);
        };

        const getReferenceTargetText = (target: ReferenceTarget) =>
            target.kind === "skillText"
                ? form.skillText
                : form.files[target.index]?.body ?? "";

        const getReferenceTargetRef = (target: ReferenceTarget) =>
            target.kind === "skillText"
                ? skillTextRef.current
                : fileBodyRefs.current[target.index];

        const updateReferenceTargetText = (
            target: ReferenceTarget,
            value: string,
        ) => {
            setForm((previous) => {
                const next =
                    target.kind === "skillText"
                        ? { ...previous, skillText: value }
                        : {
                              ...previous,
                              files: previous.files.map((file, index) =>
                                  index === target.index
                                      ? { ...file, body: value }
                                      : file,
                              ),
                          };
                return ensureReferencedToolsAllowed(
                    next,
                    store.availableToolNames,
                );
            });
        };

        const referenceTargetsMatch = (
            left: ReferenceTarget,
            right: ReferenceTarget,
        ) =>
            left.kind === right.kind &&
            (left.kind === "skillText" ||
                (right.kind === "fileBody" && left.index === right.index));

        const getCaretAbsolutePosition = (
            target: HTMLInputElement | HTMLTextAreaElement,
            caret: number,
        ) => {
            const coordinates = getCaretCoordinates(target, caret);
            const rect = target.getBoundingClientRect();
            return {
                top:
                    coordinates.top +
                    coordinates.height +
                    window.scrollY +
                    rect.top -
                    target.scrollTop,
                left: coordinates.left + window.scrollX + rect.left,
            };
        };

        const handleEditorChange = (
            event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
            target: ReferenceTarget,
        ) => {
            const oldText = getReferenceTargetText(target);
            const newText = event.target.value;
            const caret = event.target.selectionStart ?? newText.length;
            updateReferenceTargetText(target, newText);

            if (
                newText.length > oldText.length &&
                caret > 0 &&
                newText[caret - 1] === "@"
            ) {
                setReferenceMenu({
                    target,
                    startIndex: caret - 1,
                    endIndex: caret,
                    position: getCaretAbsolutePosition(event.target, caret),
                    search: "",
                });
                return;
            }

            if (
                !referenceMenu ||
                !referenceTargetsMatch(referenceMenu.target, target)
            ) {
                return;
            }

            const search = newText.slice(referenceMenu.startIndex + 1, caret);
            if (caret <= referenceMenu.startIndex || /[\r\n{}@]/.test(search)) {
                closeReferenceMenu();
                return;
            }

            setReferenceMenu({
                ...referenceMenu,
                endIndex: caret,
                search,
            });
        };

        const handleReferenceMenuKeyDown = (
            event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
        ) => {
            if (!referenceMenu) return;
            if (event.key === "Escape") {
                event.preventDefault();
                closeReferenceMenu();
                return;
            }

            if (
                referenceMenuRef.current &&
                ["ArrowUp", "ArrowDown", "Enter"].includes(event.key)
            ) {
                event.preventDefault();
                event.stopPropagation();
                const keyboardEvent = new KeyboardEvent("keydown", {
                    key: event.key,
                    code: event.code,
                    bubbles: true,
                    cancelable: true,
                });
                referenceMenuRef.current.focus();
                referenceMenuRef.current.dispatchEvent(keyboardEvent);
            }
        };

        const insertReference = (type: ReferenceType, name: string) => {
            if (!referenceMenu) return;

            const token =
                type === "skill"
                    ? `{{skill:${name}}}`
                    : type === "tool"
                    ? `{{tool:${name}}}`
                    : `{{file:${name}}}`;
            const { target, startIndex, endIndex } = referenceMenu;
            const currentText = getReferenceTargetText(target);
            const nextText =
                currentText.slice(0, startIndex) +
                token +
                currentText.slice(endIndex);
            updateReferenceTargetText(target, nextText);
            closeReferenceMenu();
            window.requestAnimationFrame(() => {
                const cursor = startIndex + token.length;
                const targetRef = getReferenceTargetRef(target);
                targetRef?.focus();
                targetRef?.setSelectionRange(cursor, cursor);
            });
        };

        const normalizedReferenceSearch =
            referenceMenu?.search.trim().toLowerCase() ?? "";
        const filteredSkillReferenceOptions = skillReferenceOptions.filter(
            (name) => name.toLowerCase().includes(normalizedReferenceSearch),
        );
        const filteredToolReferenceOptions = allowedToolOptions.filter(
            (tool) =>
                tool.name.toLowerCase().includes(normalizedReferenceSearch) ||
                tool.friendlyName
                    .toLowerCase()
                    .includes(normalizedReferenceSearch) ||
                tool.description
                    ?.toLowerCase()
                    .includes(normalizedReferenceSearch),
        );
        const filteredFileReferenceOptions = fileReferenceOptions.filter(
            (name) => name.toLowerCase().includes(normalizedReferenceSearch),
        );

        return (
            <Box
                sx={{
                    p: 3,
                    width: "100%",
                    maxWidth: "100%",
                    boxSizing: "border-box",
                }}
            >
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    spacing={2}
                    sx={{
                        mb: 2,
                        width: "100%",
                        maxWidth: 1280,
                        pr: 2,
                    }}
                >
                    <Box>
                        <Typography variant="h5">
                            {activeSkillId ? "Edit Skill" : "New Skill"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Skills give KBo expert instructions it can load for
                            a task.
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                        <AcxButton acxTheme="secondary" onClick={close}>
                            Cancel
                        </AcxButton>
                        <AcxButton
                            acxTheme="secondary"
                            onClick={() => setTestDialogOpen(true)}
                            startIcon={<PlayArrowIcon fontSize="small" />}
                            disabled={
                                saving ||
                                loading ||
                                form.skillText.trim().length === 0 ||
                                form.scopes.length === 0
                            }
                        >
                            Test Skill
                        </AcxButton>
                        <AcxButton
                            color="primary"
                            onClick={save}
                            loading={saving}
                            disabled={!isValid || saving || loading}
                            startIcon={<SaveIcon fontSize="small" />}
                        >
                            Save Skill
                        </AcxButton>
                    </Stack>
                </Stack>

                {loading ? (
                    <Box sx={{ width: "100%", py: 4 }}>
                        <AcxLoadingIndicator size={32} />
                    </Box>
                ) : (
                    <Grid
                        container
                        spacing={2}
                        sx={{ width: "100%", maxWidth: 1280 }}
                    >
                        <Grid item xs={12}>
                            <AcxMainTextField
                                id="agent-skill-name"
                                labelText="Name"
                                required
                                error={duplicateName}
                                errorHelper="A skill with this name already exists."
                                value={form.name}
                                onChange={(e) =>
                                    update({ name: e.target.value })
                                }
                                placeholderText="e.g. Refund Handling"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <AcxMainTextField
                                id="agent-skill-description"
                                labelText="Description"
                                required
                                multiline
                                rowsMin={2}
                                rowsMax={4}
                                value={form.description}
                                onChange={(e) =>
                                    update({ description: e.target.value })
                                }
                                placeholderText="When should KBo use this skill?"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <AcxSelectMulti
                                id="agent-skill-scopes"
                                options={skillScopeOptions}
                                valueField="value"
                                labelField="label"
                                defaultValue={skillScopeOptions.filter((option) =>
                                    form.scopes.includes(option.value),
                                )}
                                onChange={(scopes) =>
                                    update({
                                        scopes:
                                            scopes?.map((scope) => scope.value) ??
                                            [],
                                    })
                                }
                                inputLabel="Application Scopes"
                                placeholder="Select applications"
                                required
                                isClearable
                                fullWidth
                                containerHeight="auto"
                                error={form.scopes.length === 0}
                                helperText="Select at least one application that can use this skill."
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <AcxSwitch
                                        checked={form.isPublished}
                                        onChange={(event) =>
                                            update({
                                                isPublished:
                                                    event.target.checked,
                                            })
                                        }
                                    />
                                }
                                label="Published in KBo"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Paper
                                variant="outlined"
                                sx={{ overflow: "hidden", width: "100%" }}
                            >
                                <Box
                                    sx={(theme) => ({
                                        display: "grid",
                                        gridTemplateColumns: {
                                            xs: "1fr",
                                            md: "280px minmax(0, 1fr)",
                                        },
                                        minHeight: { md: 620 },
                                        bgcolor: theme.palette.background.paper,
                                    })}
                                >
                                    <Box
                                        sx={(theme) => ({
                                            p: 1.5,
                                            borderRight: {
                                                xs: 0,
                                                md: `1px solid ${theme.palette.divider}`,
                                            },
                                            borderBottom: {
                                                xs: `1px solid ${theme.palette.divider}`,
                                                md: 0,
                                            },
                                            bgcolor: theme.palette.action.hover,
                                            minWidth: 0,
                                        })}
                                    >
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            spacing={1}
                                        >
                                            <Typography fontWeight={600}>
                                                Files
                                            </Typography>
                                            <Tooltip title="Add file">
                                                <IconButton
                                                    size="small"
                                                    aria-label="Add file"
                                                    onClick={addFile}
                                                >
                                                    <AddIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                        <List
                                            dense
                                            disablePadding
                                            sx={{ mt: 1 }}
                                        >
                                            <ListItemButton
                                                selected={
                                                    selectedFileKey === "skill"
                                                }
                                                onClick={() =>
                                                    setSelectedFileKey("skill")
                                                }
                                                sx={{ borderRadius: 1 }}
                                            >
                                                <ListItemIcon
                                                    sx={{ minWidth: 32 }}
                                                >
                                                    <DescriptionIcon fontSize="small" />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary="SKILL.md"
                                                    secondary={
                                                        form.skillText.trim()
                                                            ? `${form.skillText.length} chars`
                                                            : "Required"
                                                    }
                                                    primaryTypographyProps={{
                                                        noWrap: true,
                                                    }}
                                                    secondaryTypographyProps={{
                                                        noWrap: true,
                                                        color: form.skillText.trim()
                                                            ? "text.secondary"
                                                            : "error",
                                                    }}
                                                />
                                            </ListItemButton>
                                            {form.files.map((file, index) => {
                                                const issue =
                                                    filePathIssues[index];
                                                return (
                                                    <ListItemButton
                                                        key={`${file.relativePath}-${index}`}
                                                        selected={
                                                            selectedFileKey ===
                                                            `file:${index}`
                                                        }
                                                        onClick={() =>
                                                            setSelectedFileKey(
                                                                `file:${index}`,
                                                            )
                                                        }
                                                        sx={{ borderRadius: 1 }}
                                                    >
                                                        <ListItemIcon
                                                            sx={{
                                                                minWidth: 32,
                                                            }}
                                                        >
                                                            <InsertDriveFileIcon
                                                                fontSize="small"
                                                                color={
                                                                    issue
                                                                        ? "error"
                                                                        : "inherit"
                                                                }
                                                            />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={
                                                                file.relativePath?.trim() ||
                                                                "Untitled file"
                                                            }
                                                            secondary={
                                                                issue ??
                                                                `${
                                                                    file.body
                                                                        ?.length ??
                                                                    0
                                                                } chars`
                                                            }
                                                            primaryTypographyProps={{
                                                                noWrap: true,
                                                            }}
                                                            secondaryTypographyProps={{
                                                                noWrap: true,
                                                                color: issue
                                                                    ? "error"
                                                                    : "text.secondary",
                                                            }}
                                                        />
                                                    </ListItemButton>
                                                );
                                            })}
                                        </List>
                                    </Box>
                                    <Box
                                        sx={{
                                            p: 2,
                                            minWidth: 0,
                                        }}
                                    >
                                        <Stack spacing={2}>
                                            <Stack
                                                direction="row"
                                                alignItems="flex-start"
                                                justifyContent="space-between"
                                                spacing={1.5}
                                            >
                                                {selectedFileIndex === null ? (
                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Typography
                                                            fontWeight={600}
                                                            noWrap
                                                        >
                                                            SKILL.md
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Box
                                                        sx={{
                                                            flex: 1,
                                                            minWidth: 0,
                                                        }}
                                                    >
                                                        <AcxMainTextField
                                                            id={`agent-skill-file-path-${selectedFileIndex}`}
                                                            labelText="Path"
                                                            required
                                                            value={
                                                                selectedFile?.relativePath ??
                                                                ""
                                                            }
                                                            error={Boolean(
                                                                selectedFilePathIssue,
                                                            )}
                                                            errorHelper={
                                                                selectedFilePathIssue ??
                                                                undefined
                                                            }
                                                            onChange={(e) =>
                                                                updateFile(
                                                                    selectedFileIndex,
                                                                    {
                                                                        relativePath:
                                                                            e
                                                                                .target
                                                                                .value,
                                                                    },
                                                                )
                                                            }
                                                            placeholderText="references/search-terms.md"
                                                        />
                                                    </Box>
                                                )}
                                                {selectedFileIndex !== null && (
                                                    <Tooltip title="Delete file">
                                                        <IconButton
                                                            aria-label="Delete file"
                                                            onClick={() =>
                                                                removeFile(
                                                                    selectedFileIndex,
                                                                )
                                                            }
                                                            sx={{ mt: 3 }}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                            {selectedFileIndex === null ? (
                                                <>
                                                    <AcxMainTextField
                                                        id="agent-skill-text"
                                                        labelText="Skill Text"
                                                        required
                                                        ref={skillTextRef}
                                                        multiline
                                                        rowsMin={20}
                                                        rowsMax={36}
                                                        error={
                                                            referenceIssues.length >
                                                            0
                                                        }
                                                        errorHelper={
                                                            referenceIssues[0]
                                                        }
                                                        helperText={
                                                            referenceIssues[0] ??
                                                            referenceHint
                                                        }
                                                        value={
                                                            selectedEditorValue
                                                        }
                                                        onChange={(event) =>
                                                            handleEditorChange(
                                                                event,
                                                                selectedReferenceTarget,
                                                            )
                                                        }
                                                        onKeyDown={
                                                            handleReferenceMenuKeyDown
                                                        }
                                                        placeholderText="The instructions KBo follows when this skill is loaded."
                                                    />
                                                    <Autocomplete
                                                        multiple
                                                        id="agent-skill-allowed-tools"
                                                        options={
                                                            allowedToolOptions
                                                        }
                                                        value={
                                                            selectedAllowedToolOptions
                                                        }
                                                        loading={toolsLoading}
                                                        filterSelectedOptions
                                                        getOptionLabel={(
                                                            option,
                                                        ) =>
                                                            option.friendlyName
                                                        }
                                                        isOptionEqualToValue={(
                                                            option,
                                                            value,
                                                        ) =>
                                                            option.name.toLowerCase() ===
                                                            value.name.toLowerCase()
                                                        }
                                                        onChange={(_, value) =>
                                                            setForm(
                                                                (previous) =>
                                                                    ensureReferencedToolsAllowed(
                                                                        {
                                                                            ...previous,
                                                                            frontmatter:
                                                                                {
                                                                                    ...(previous.frontmatter ??
                                                                                        {}),
                                                                                    allowedTools:
                                                                                        formatAllowedTools(
                                                                                            value.map(
                                                                                                (
                                                                                                    tool,
                                                                                                ) =>
                                                                                                    tool.name,
                                                                                            ),
                                                                                        ),
                                                                                },
                                                                        },
                                                                        store.availableToolNames,
                                                                    ),
                                                            )
                                                        }
                                                        renderOption={(
                                                            props,
                                                            option,
                                                        ) => {
                                                            const {
                                                                key,
                                                                ...optionProps
                                                            } = props as typeof props & {
                                                                key?: React.Key;
                                                            };
                                                            return (
                                                                <Box
                                                                    component="li"
                                                                    key={
                                                                        key ??
                                                                        option.name
                                                                    }
                                                                    {...optionProps}
                                                                    sx={{
                                                                        alignItems:
                                                                            "flex-start",
                                                                        py: 1,
                                                                    }}
                                                                >
                                                                    <Box>
                                                                        <Typography variant="body2">
                                                                            {
                                                                                option.friendlyName
                                                                            }
                                                                        </Typography>
                                                                        {option.description && (
                                                                            <Typography
                                                                                variant="caption"
                                                                                color="text.secondary"
                                                                                sx={{
                                                                                    display:
                                                                                        "block",
                                                                                }}
                                                                            >
                                                                                {
                                                                                    option.description
                                                                                }
                                                                            </Typography>
                                                                        )}
                                                                    </Box>
                                                                </Box>
                                                            );
                                                        }}
                                                        renderInput={(
                                                            params,
                                                        ) => (
                                                            <TextField
                                                                {...params}
                                                                label="Allowed Tools"
                                                                placeholder="Select tools"
                                                            />
                                                        )}
                                                    />
                                                </>
                                            ) : (
                                                <AcxMainTextField
                                                    id={`agent-skill-file-body-${selectedFileIndex}`}
                                                    labelText="File Body"
                                                    ref={(element) => {
                                                        fileBodyRefs.current[
                                                            selectedFileIndex
                                                        ] = element;
                                                    }}
                                                    multiline
                                                    rowsMin={23}
                                                    rowsMax={38}
                                                    value={selectedEditorValue}
                                                    error={
                                                        referenceIssues.length >
                                                        0
                                                    }
                                                    errorHelper={
                                                        referenceIssues[0]
                                                    }
                                                    helperText={
                                                        referenceIssues[0] ??
                                                        referenceHint
                                                    }
                                                    onChange={(event) =>
                                                        handleEditorChange(
                                                            event,
                                                            selectedReferenceTarget,
                                                        )
                                                    }
                                                    onKeyDown={
                                                        handleReferenceMenuKeyDown
                                                    }
                                                    placeholderText="Reference material for this skill."
                                                />
                                            )}
                                        </Stack>
                                    </Box>
                                </Box>
                            </Paper>
                        </Grid>
                        <AcxMenuV2
                            open={referenceMenu !== null}
                            onClose={closeReferenceMenu}
                            anchorReference="anchorPosition"
                            anchorPosition={referenceMenu?.position}
                            anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "left",
                            }}
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "left",
                            }}
                            disableAutoFocus
                            disableAutoFocusItem
                            disableEnforceFocus
                            MenuListProps={{
                                ref: referenceMenuRef,
                                tabIndex: -1,
                            }}
                            slotProps={{
                                paper: {
                                    sx: { maxHeight: 360, width: 360 },
                                },
                            }}
                        >
                            <Typography
                                component="div"
                                sx={(theme) => ({
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 1,
                                    px: 2,
                                    py: 1,
                                    fontSize: 13,
                                    fontWeight: 600,
                                    bgcolor: theme.palette.background.paper,
                                })}
                            >
                                Skills
                            </Typography>
                            {filteredSkillReferenceOptions.length > 0 ? (
                                filteredSkillReferenceOptions.map((name) => (
                                    <AcxMenuItem
                                        key={`skill-${name}`}
                                        id={`agent-skill-reference-skill-${name}`}
                                        onClick={() =>
                                            insertReference("skill", name)
                                        }
                                    >
                                        {name}
                                    </AcxMenuItem>
                                ))
                            ) : (
                                <AcxMenuItem
                                    id="agent-skill-reference-no-skills"
                                    disabled
                                >
                                    No matching skills
                                </AcxMenuItem>
                            )}
                            <Typography
                                component="div"
                                sx={(theme) => ({
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 1,
                                    px: 2,
                                    py: 1,
                                    fontSize: 13,
                                    fontWeight: 600,
                                    bgcolor: theme.palette.background.paper,
                                })}
                            >
                                Tools
                            </Typography>
                            {filteredToolReferenceOptions.length > 0 ? (
                                filteredToolReferenceOptions.map((tool) => (
                                    <AcxMenuItem
                                        key={`tool-${tool.name}`}
                                        id={`agent-skill-reference-tool-${tool.name}`}
                                        onClick={() =>
                                            insertReference("tool", tool.name)
                                        }
                                    >
                                        <Box>
                                            <Typography variant="body2">
                                                {tool.friendlyName}
                                            </Typography>
                                            {tool.description && (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ display: "block" }}
                                                >
                                                    {tool.description}
                                                </Typography>
                                            )}
                                        </Box>
                                    </AcxMenuItem>
                                ))
                            ) : (
                                <AcxMenuItem
                                    id="agent-skill-reference-no-tools"
                                    disabled
                                >
                                    No matching tools
                                </AcxMenuItem>
                            )}
                            <Typography
                                component="div"
                                sx={(theme) => ({
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 1,
                                    px: 2,
                                    py: 1,
                                    fontSize: 13,
                                    fontWeight: 600,
                                    bgcolor: theme.palette.background.paper,
                                })}
                            >
                                Files
                            </Typography>
                            {filteredFileReferenceOptions.length > 0 ? (
                                filteredFileReferenceOptions.map((name) => (
                                    <AcxMenuItem
                                        key={`file-${name}`}
                                        id={`agent-skill-reference-file-${name}`}
                                        onClick={() =>
                                            insertReference("file", name)
                                        }
                                    >
                                        {name}
                                    </AcxMenuItem>
                                ))
                            ) : (
                                <AcxMenuItem
                                    id="agent-skill-reference-no-files"
                                    disabled
                                >
                                    No matching files
                                </AcxMenuItem>
                            )}
                        </AcxMenuV2>
                    </Grid>
                )}
                <AgentSkillTestDialog
                    open={testDialogOpen}
                    skillId={activeSkillId ?? undefined}
                    skill={currentRequest()}
                    onClose={() => setTestDialogOpen(false)}
                    onTestCasesChange={(testCases) => update({ testCases })}
                    onVersionSaved={handleDatasetVersionSaved}
                />
            </Box>
        );
    },
);

export default AgentSkillEditorPage;
